import type React from 'react';
import { useCallback, useRef } from 'react';

import { MAX_FILESTACK_FILE_SIZE, mentionType } from '@aline/common';
import { useVersionAwareUpload } from 'src/helpers/docx-upload-target';
import { getVersionAwareUploadFailureMessage } from 'src/helpers/docx-upload-target/get-version-aware-upload-failure-message';
import { aiChatAcceptedMimeTypes, extractFileName } from 'src/helpers/integrations/filestack';
import { useNotificationDispatch } from 'src/hooks';
import { useFileDragState } from 'src/hooks/drag-and-drop';
import type { FileDragProps } from 'src/hooks/drag-and-drop';
import { NotificationsActionType } from 'src/notifications';
import { v4 as uuid4 } from 'uuid';

import type { AlineMarkdownEditorRef, DocumentMentionElement } from '../ChatPanel/ChatEditor';
import { insertMentionAt, MAX_CHAT_UPLOAD_FILES } from '../ChatPanel/ChatEditor';

interface UseChatEditorDropzoneOptions {
  editorRef: React.RefObject<AlineMarkdownEditorRef | null>;
  disabled?: boolean;
  maxFiles?: number;
}

interface UseChatEditorDropzoneReturn {
  dropzoneProps: FileDragProps & {
    onPaste: (e: React.ClipboardEvent) => void;
  };
  /** react-dropzone's root ref; already included in dropzoneProps. Exposed so consumers can read the dropzone element (e.g. as a popover anchor). */
  dropzoneRootRef: React.RefObject<HTMLElement>;
  isDragging: boolean;
  versionDialog: JSX.Element;
}

export function useChatEditorDropzone(
  options: UseChatEditorDropzoneOptions
): UseChatEditorDropzoneReturn {
  const isUploadingRef = useRef(false);
  const dispatch = useNotificationDispatch();
  const { uploadFiles: uploadWithVersionMatch, versionDialog } = useVersionAwareUpload();
  const { disabled = false, editorRef, maxFiles = MAX_CHAT_UPLOAD_FILES } = options;

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (disabled || isUploadingRef.current) return;

      const editor = editorRef.current?.getEditor();
      if (!editor) return;

      const validFiles = files.filter(file => {
        return file.size <= MAX_FILESTACK_FILE_SIZE && isFileAccepted(file);
      });
      const invalidFileCount = files.length - validFiles.length;
      if (invalidFileCount > 0) {
        dispatch({
          payload: {
            key: uuid4(),
            message: `Only Word and PDF files are supported. Skipping ${invalidFileCount} file${invalidFileCount > 1 ? 's' : ''}.`,
            variant: 'warning',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
      }

      const filesToUpload = validFiles.slice(0, maxFiles);
      if (!filesToUpload.length) return;

      const filesOverMaxCount = validFiles.length - maxFiles;
      if (filesOverMaxCount > 0) {
        dispatch({
          payload: {
            key: uuid4(),
            message: `You can only upload up to ${maxFiles} files at a time. Skipping ${filesOverMaxCount} file${filesOverMaxCount > 1 ? 's' : ''}.`,
            variant: 'warning',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
      }

      isUploadingRef.current = true;
      const tempIdByFile = new Map(
        filesToUpload.map(file => {
          const tempId = uuid4();
          const filename = extractFileName(file.name);

          // Show a placeholder while the upload is pending.
          insertMentionAt(editor, {
            mentionNode: {
              children: [{ text: '' }],
              documentId: tempId,
              documentName: filename,
              isPlaceholder: true,
              mentionTypeName: 'document',
              type: mentionType,
              value: filename,
            } satisfies DocumentMentionElement,
          });

          return [file, tempId];
        })
      );

      try {
        const { failedFiles, uploadedFiles } = await uploadWithVersionMatch(filesToUpload);
        resolvePlaceholderMentions(editor, { tempIdByFile, uploadedFiles });

        const failureMessage = getVersionAwareUploadFailureMessage({
          failedCount: failedFiles.length,
          uploadedCount: uploadedFiles.length,
        });
        if (failureMessage)
          dispatch({
            payload: { key: uuid4(), message: failureMessage, variant: 'error' },
            type: NotificationsActionType.PUSH_NOTIFICATION,
          });
      } catch (error) {
        for (const tempId of tempIdByFile.values()) {
          removePlaceholderMention(editor, tempId);
        }
        dispatch({
          payload: {
            key: uuid4(),
            message: error instanceof Error ? error.message : 'Upload failed.',
            variant: 'error',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
      } finally {
        isUploadingRef.current = false;
      }
    },
    [disabled, maxFiles, uploadWithVersionMatch, dispatch, editorRef]
  );

  const handleDropFiles = useCallback(
    (files: File[]) => {
      void uploadFiles(files);
    },
    [uploadFiles]
  );

  const { dragProps, isDragging, rootRef } = useFileDragState({
    disabled,
    onDropFiles: handleDropFiles,
  });

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const pastedFiles = Array.from(e.clipboardData?.files ?? []);
      if (pastedFiles.length) {
        e.preventDefault();
        void uploadFiles(pastedFiles);
      }
      // If no files, let the default paste behavior handle text...
    },
    [uploadFiles]
  );

  return {
    dropzoneProps: { ...dragProps, onPaste: handlePaste },
    dropzoneRootRef: rootRef,
    isDragging,
    versionDialog,
  };
}

function isFileAccepted(file: File): boolean {
  return aiChatAcceptedMimeTypes.has(file.type);
}

function resolvePlaceholderMentions(
  editor: ReturnType<AlineMarkdownEditorRef['getEditor']>,
  options: {
    tempIdByFile: ReadonlyMap<File, string>;
    uploadedFiles: { documentId: string; file: File; name: string }[];
  }
) {
  const uploadedByFile = new Map(options.uploadedFiles.map(uploaded => [uploaded.file, uploaded]));
  for (const [file, tempId] of options.tempIdByFile) {
    const uploaded = uploadedByFile.get(file);
    if (uploaded)
      updatePlaceholderMention(editor, {
        document: { id: uploaded.documentId, name: uploaded.name },
        tempId,
      });
    else {
      removePlaceholderMention(editor, tempId);
    }
  }
}

function findMentionPath(editor: ReturnType<AlineMarkdownEditorRef['getEditor']>, tempId: string) {
  const entry = editor.api
    .nodes({
      at: [],
      match: n => n.type === mentionType && n.documentId === tempId,
    })
    .next().value;
  return entry?.[1];
}

function updatePlaceholderMention(
  editor: ReturnType<AlineMarkdownEditorRef['getEditor']>,
  options: { document: { id: string; name: string }; tempId: string }
): void {
  const path = findMentionPath(editor, options.tempId);
  if (!path) return;

  editor.tf.setNodes(
    {
      documentId: options.document.id,
      documentName: options.document.name,
      isPlaceholder: false,
      value: options.document.name,
    } satisfies Pick<
      DocumentMentionElement,
      'documentId' | 'documentName' | 'isPlaceholder' | 'value'
    >,
    { at: path, voids: true }
  );
}

function removePlaceholderMention(
  editor: ReturnType<AlineMarkdownEditorRef['getEditor']>,
  tempId: string
) {
  const path = findMentionPath(editor, tempId);
  if (!path) return;
  editor.tf.removeNodes({ at: path });
}
